import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { Pagination } from "swiper/modules";
import NavigateBack from "../NavigateBack";
import { motion } from "framer-motion";
// import StatusChangeModal from "../FunctionalComponents/StatusChangeModal";
// import Orders from "./Orders";
import { getProducts, getCategories, getCoupons, getShowcaseSections, getDeliveryOptions, ActiveAndOutOfStock, fetchGstGroups, deleteProduct } from "../../services/ClientService"; // Import API functions
import { FaChevronDown, FaChevronUp, FaTrash, FaExclamationTriangle } from "react-icons/fa";
import ConfirmModal from "../ConfirmModal";
import { toast } from "react-toastify";

const ProductOverview = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isActive, setIsActive] = useState(true);
    const [isOutOfStock, setIsOutOfStock] = useState(false);

    // Helper function to get all product images with fallback to option-specific images
    const getProductImages = (product) => {
        if (!product) return [];
        // Check main images first
        if (product.images && product.images.length > 0) {
            return product.images;
        }
        // Fallback to first option's images
        if (product.productOptions && product.productOptions.length > 0) {
            const firstOption = product.productOptions[0];
            if (firstOption.images && firstOption.images.length > 0) {
                return firstOption.images;
            }
        }
        // Return empty array if no images found
        return [];
    };
    // const [showStatusModal, setShowStatusModal] = useState(false);
    // const [selectedStatus, setSelectedStatus] = useState("Received");
    // const [activeTab, setActiveTab] = useState("Received");
    // const [expandedCard, setExpandedCard] = useState(null);
    const [categoriesData, setCategoriesData] = useState(null);
    const [couponsData, setCouponsData] = useState(null);
    const [deliveryOptionsData, setDeliveryOptionsData] = useState([]);
    const [sectionsData, setSectionsData] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [showFullDescription, setShowFullDescription] = useState(false);
    const [gstGroups, setGstGroups] = useState([]); // GST groups state
    const [gstGroupLoading, setGstGroupLoading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    useEffect(() => {
        const fetchProductData = async () => {
            try {
                setLoading(true);
                const data = await getProducts({ "_id": id });
                if (data.success) {
                    if (data.data && data.data.length > 0) {
                        setProduct(data.data[0]);
                        fetchCategories(data.data[0].category);
                        fetchCoupons(data.data[0].coupons);
                        fetchSections(data.data[0].sections);
                        fetchDeliveryOptions(data.data[0].deliveryOptions);
                        setIsActive(data.data[0].isActive);
                        setIsOutOfStock(data.data[0].isOutOfStock);

                    } else {
                        console.warn("No product data found for ID:", id);
                    }
                } else {
                    console.warn("Failed to fetch product:", data.message);
                }
            } catch (error) {
                console.warn("Error fetching product:", error);
            } finally {
                setLoading(false);
            }
        };

        const fetchCategories = async (categoryId) => {
            if (categoryId) {
                try {
                    // Fetch all categories to search for the categoryId in the hierarchy
                    const allCategories = await getCategories({});
                    if (allCategories.success && allCategories.data) {
                        let foundCategory = null;
                        
                        for (const parent of allCategories.data) {
                            // Check if the categoryId matches a parent category
                            if (parent._id === categoryId) {
                                foundCategory = { categoryName: parent.categoryName };
                                break;
                            }
                            // Check if the categoryId matches a subcategory
                            if (parent.subcategories?.length) {
                                const sub = parent.subcategories.find(s => s._id === categoryId);
                                if (sub) {
                                    foundCategory = { 
                                        categoryName: `${parent.categoryName} > ${sub.categoryName}`,
                                        parentName: parent.categoryName,
                                        subcategoryName: sub.categoryName
                                    };
                                    break;
                                }
                            }
                        }
                        
                        setCategoriesData(foundCategory || { categoryName: 'Uncategorized' });
                    } else {
                        console.warn("Failed to fetch categories");
                        setCategoriesData({ categoryName: 'Uncategorized' });
                    }
                } catch (error) {
                    console.warn("Error fetching category:", error);
                    setCategoriesData({ categoryName: 'Uncategorized' });
                }
            }
        };

        const fetchCoupons = async (couponIds) => {
            if (couponIds && couponIds.length > 0) {
                try {
                    const couponsData = await getCoupons({ "_id": couponIds });
                    if (couponsData.success) {
                        setCouponsData(couponsData.data);
                    } else {
                        console.warn("Failed to fetch coupons:", couponsData.message);
                    }
                } catch (error) {
                    console.warn("Error fetching coupons:", error);
                }
            }
        };

        const fetchSections = async (sectionIds) => {
            if (sectionIds && sectionIds.length > 0) {
                try {
                    const sections = await getShowcaseSections({ "_id": sectionIds });
                    if (sections.success) {
                        setSectionsData(sections.data);
                    } else {
                        console.warn("Failed to fetch showcase sections:", sections.message);
                    }
                } catch (error) {
                    console.warn("Error fetching showcase sections:", error);
                }
            }
        };

        const fetchDeliveryOptions = async (params) => {
            try {
                const data = await getDeliveryOptions(params);
                if (data.success) {
                    const user = JSON.parse(localStorage.getItem("userData"));
                    let formattedOptions = [];
                    if (data?.subPlanData?.enableShipRocket) {
                        formattedOptions = data.data.map(option => ({
                            value: option.value, // Unique identifier
                            label: option.label, // Display label
                        }));
                    } else {
                        formattedOptions = data.data.map(option => ({
                            value: option.deliveryOptionName.value, // Unique identifier
                            label: option.deliveryOptionName.label, // Display label
                            message: option.message,
                            kmsRange: option.kmsRange,
                            rangeOptions: option.options ?? [],
                        }));
                    }
                    // Filter options to include only those saved in the product
                    const filteredOptions = formattedOptions.filter(option =>
                        params.includes(option.value)
                    );
                    setDeliveryOptionsData(filteredOptions);
                } else {
                    setDeliveryOptionsData([]);
                }
            } catch (error) {
                setDeliveryOptionsData([]);
                console.warn("Error fetching Delivery Options:", error);
            }
        };

        const fetchGstGroupsData = async () => {
            setGstGroupLoading(true);
            try {
                const data = await fetchGstGroups();
                if (data.success && data.data) {
                    const formattedGroups = data.data[0].gstGroups.map(group => ({
                        value: group._id,
                        label: group.name,
                        ...group
                    }));
                    setGstGroups(formattedGroups);
                } else {
                    setGstGroups([]);
                }
            } catch (error) {
                setGstGroups([]);
            } finally {
                setGstGroupLoading(false);
            }
        };

        fetchProductData();
        fetchGstGroupsData();
    }, [id]);

    // const handleChangeStatus = (newStatus) => {
    //     console.log("Status changed to:", newStatus);
    //     setShowStatusModal(false);
    // };

    const handleActiveAndOutOfStock = async (key, value) => {
        try {
            if (key === 'isActive') setIsActive(!isActive)
            else if (key === 'isOutOfStock') setIsOutOfStock(!isOutOfStock)
            else { console.log("type not Match"); return; }
            await ActiveAndOutOfStock({ key, value, "_id": id });
            toast.success(`Product ${key === 'isActive' ? (value ? 'activated' : 'deactivated') : (value ? 'marked out of stock' : 'marked in stock')} successfully!`);
        } catch (error) {
            console.error('Error saving handleActiveAndOutOfStock:', { [key]: value });
            toast.error('Failed to update product status');
        }
    };

    const handleDeleteProduct = async () => {
        try {
            setDeleteLoading(true);
            const response = await deleteProduct({ _id: id });
            if (response.success) {
                toast.success('Product deleted successfully!');
                setShowDeleteModal(false);
                navigate('/products');
            } else {
                toast.error(response.message || 'Failed to delete product');
            }
        } catch (error) {
            console.error('Error deleting product:', error);
            toast.error('Failed to delete product. Please try again.');
        } finally {
            setDeleteLoading(false);
        }
    };


    return (
        <div className="h-auto">
            {loading ? (
                <div className="fixed inset-0 flex items-center justify-center bg-white/80 z-50">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                </div>
            ) : (
                <div>
                    <NavigateBack target="/products" className="pl-3" />

                    {/* Status Header */}
                    <div className="p-6 mb-6 bg-white/90 backdrop-blur-lg rounded-bl-2xl rounded-br-2xl shadow-xl border border-gray-100/50 flex flex-col md:flex-row items-center justify-between">
                        <div className="w-full flex flex-col sm:flex-row items-center gap-4 md:gap-8 justify-between">
                            <div className="flex items-center gap-3">
                                <div className={`w-3 h-3 rounded-full ${isActive ? 'bg-emerald-400' : 'bg-rose-400'}`} />
                                <span className="text-lg font-bold text-gray-800">Product Status</span>
                            </div>
                            <div className="flex gap-6 items-center">
                                {/* Active Toggle */}
                                <div className="flex items-center">
                                    <span className="mr-3 text-[16px] font-semibold text-gray-700">
                                        {isActive ? 'Active' : 'Inactive'}
                                    </span>
                                    <label className="relative inline-block w-14 h-7">
                                        <input
                                            type="checkbox"
                                            checked={isActive}
                                            onChange={() => handleActiveAndOutOfStock('isActive', !isActive)}
                                            className="opacity-0 w-0 h-0"
                                        />
                                        <span
                                            className={`absolute cursor-pointer inset-0 transition-colors duration-300 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-rose-500'
                                                }`}
                                        ></span>
                                        <span
                                            className={`absolute left-1 top-1 w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-300 transform ${isActive ? 'translate-x-7' : 'translate-x-0'
                                                }`}
                                        ></span>
                                    </label>
                                </div>
                                {/* Out of Stock Toggle */}
                                <div className="flex items-center">
                                    <span className="mr-3 text-[16px] font-semibold text-gray-700">
                                        {isOutOfStock ? 'Out of Stock' : 'In Stock'}
                                    </span>
                                    <label className="relative inline-block w-14 h-7">
                                        <input
                                            type="checkbox"
                                            checked={isOutOfStock}
                                            onChange={() => handleActiveAndOutOfStock('isOutOfStock', !isOutOfStock)}
                                            className="opacity-0 w-0 h-0"
                                        />
                                        <span className={`absolute cursor-pointer inset-0 transition-colors duration-300 rounded-full ${isOutOfStock ? 'bg-amber-500' : 'bg-gray-300'
                                            }`}
                                        ></span>
                                        <span
                                            className={`absolute left-1 top-1 w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-300 transform ${isOutOfStock ? 'translate-x-7' : 'translate-x-0'
                                                }`}
                                        ></span>
                                    </label>
                                </div>
                            </div>


                            <div className="flex items-center gap-3 mt-4 md:mt-0">
                                <button
                                    onClick={() => navigate(`/editProduct`, { state: { ...product, gstGroups } })}
                                    className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-full hover:shadow-lg transition-all"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                    </svg>
                                    Edit Product
                                </button>
                                <button
                                    onClick={() => setShowDeleteModal(true)}
                                    className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-full hover:shadow-lg transition-all"
                                >
                                    <FaTrash className="w-4 h-4" />
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>


                    {product && (
                        <div className="bg-gradient-to-br from-gray-50 to-blue-50 min-h-fit">
                            <div className="p-6">
                                {/* Product ID */}
                                <div className="mb-6 px-4 py-2 bg-gray-900 text-gray-100 rounded-full w-fit inline-block">
                                    <span className="text-sm">Product ID : </span>
                                    <code className="font-mono text-blue-300">{product.productId}</code>
                                </div>

                                {/* Main Product Section */}
                                <div className="grid grid-cols-1 md:grid-cols-[0.9fr_1.1fr] gap-8 mb-8">
                                    {/* Image Gallery */}
                                    <div className="relative group overflow-hidden h-full">
                                        <Swiper
                                            modules={[Pagination]}
                                            pagination={{ clickable: true, dynamicBullets: true }}
                                        >
                                            {getProductImages(product).length > 0 ? (
                                                getProductImages(product).map((imageData, i) => (
                                                    <SwiperSlide key={i}>
                                                        <div className="relative">
                                                            <img
                                                                src={imageData.preview}
                                                                alt="Product"
                                                                className="w-full md:max-h-[500px] max-md:h-[200px] object-contain object-center"
                                                            />
                                                        </div>
                                                    </SwiperSlide>
                                                ))
                                            ) : (
                                                <SwiperSlide>
                                                    <div className="relative">
                                                        <img
                                                            src={require("../../../src/no-image-icon-23494.png")}
                                                            alt="No Image"
                                                            className="w-full md:max-h-[500px] max-md:h-[200px] object-contain object-center"
                                                        />
                                                    </div>
                                                </SwiperSlide>
                                            )}
                                        </Swiper>

                                    </div>

                                    {/* Product Info */}
                                    <div className="space-y-6">
                                        <div className="pb-6 border-b border-gray-200">
                                            <div className="flex flex-wrap items-center gap-2 mb-4">
                                                <h1 className="text-3xl font-bold text-gray-900">{product.productName}</h1>
                                                {product.isMarketplaceProduct && (
                                                    <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-violet-100 text-violet-700 border border-violet-200">
                                                        Marketplace (B2B)
                                                    </span>
                                                )}
                                            </div>
                                            <p className={`text-lg text-gray-600 leading-relaxed ${showFullDescription ? '' : 'line-clamp-4'}`}>
                                                {product.description}
                                            </p>
                                            <button
                                                onClick={() => setShowFullDescription(!showFullDescription)}
                                                className="mt-1 text-blue-500 hover:underline"
                                            >
                                                {showFullDescription ? 'Show Less' : 'Show More'}
                                            </button>
                                            {/* Shoecases sections */}
                                            <div className="flex flex-wrap items-center gap-4 mt-4">
                                                {sectionsData && sectionsData.map((section, i) => (
                                                    <motion.div
                                                        key={i}
                                                        className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-lg transition-transform duration-300"
                                                        style={{
                                                            borderLeft: "12px solid",
                                                            borderImageSlice: 1,
                                                            borderImageSource: "linear-gradient(to bottom, #ff7e5f, #feb47b)"
                                                        }}
                                                        whileHover={{ scale: 1.05, rotate: 2 }}
                                                        transition={{ type: "spring", stiffness: 300 }}
                                                    >
                                                        <span className="text-sm font-medium text-gray-800">{section.sectionName}</span>
                                                    </motion.div>
                                                ))}
                                            </div>
                                            {/* Category label below product name */}
                                            <div
                                                className="mt-4 inline-block text-transparent bg-clip-text bg-gradient-to-r from-pink-500 via-orange-400 to-yellow-400 font-semibold text-base tracking-wide drop-shadow-sm"
                                                style={{
                                                    letterSpacing: "0.04em",
                                                    textShadow: "0 1px 8px rgba(255, 186, 73, 0.15), 0 1px 1px rgba(0,0,0,0.04)"
                                                }}
                                            >
                                                {categoriesData?.categoryName || 'Uncategorized'}
                                            </div>

                                        </div>

                                        {/* Pricing Matrix */}
                                        <div className="space-y-4">
                                            <h3 className="text-xl font-bold text-gray-800">Available Variants</h3>
                                            {product.productOptions.map((option, i) => {
                                                const gstGroup = gstGroups.find(g => g.value === product.gstGroupId);
                                                const gstPercent = gstGroup?.percentage || 0;
                                                const gstType = gstGroup?.type || "exclusive";
                                                const gstTypeLabel = gstType === "exclusive" ? "Exclusive GST" : "Inclusive GST";
                                                const priceAfterDiscount = Number(option.discountPrice);
                                                let gstAmount = 0, cgst = 0, sgst = 0, basePrice = 0, totalPayable = 0, taxPortion = 0;
                                                if (gstType === "exclusive") {
                                                    gstAmount = priceAfterDiscount * (gstPercent / 100);
                                                    cgst = sgst = gstAmount / 2;
                                                    totalPayable = priceAfterDiscount + gstAmount;
                                                } else {
                                                    taxPortion = priceAfterDiscount * (gstPercent / (100 + gstPercent));
                                                    cgst = sgst = taxPortion / 2;
                                                    basePrice = priceAfterDiscount - taxPortion;
                                                    totalPayable = priceAfterDiscount;
                                                }
                                                return (
                                                    <div key={i} className="p-4 bg-white rounded-xl flex flex-col gap-2 mb-2 border border-gray-100 shadow-sm">
                                                        <div className="flex justify-between items-center mb-1">
                                                            <span className="font-semibold text-lg text-gray-700">
                                                                {typeof option.weight === 'object' && option.weight !== null 
                                                                    ? option.weight.weightLabel || `${option.weight.weightValue} ${option.weight.metricLabel}`
                                                                    : option.weight}
                                                            </span>
                                                            <span className="text-xl font-bold text-green-600 bg-green-50 px-3 py-1 rounded-lg">₹{option.discountPrice}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${gstType === 'exclusive' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'}`}>{gstTypeLabel}</span>
                                                            <span className="text-xs text-gray-400">GST {gstPercent}%</span>
                                                        </div>
                                                        <div className="flex flex-col md:flex-row md:items-center md:gap-6 text-sm text-gray-700 bg-gray-50 rounded-lg p-3">
                                                            {gstType === "exclusive" ? (
                                                                <>
                                                                    <div className="flex-1 flex flex-col gap-1">
                                                                        <span>GST: <b>₹{gstAmount.toFixed(2)}</b></span>
                                                                        <span className="text-xs text-gray-500">CGST: ₹{cgst.toFixed(2)} | SGST: ₹{sgst.toFixed(2)}</span>
                                                                    </div>
                                                                    <div className="flex-1 flex flex-col gap-1 border-l border-gray-200 pl-4 mt-2 md:mt-0">
                                                                        <span>Total Payable: <b className="text-blue-700">₹{Math.round(totalPayable)}</b></span>
                                                                        <span className="text-xs text-gray-400">(Price after discount + GST, rounded)</span>
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <div className="flex-1 flex flex-col gap-1">
                                                                        <span>GST Portion: <b>₹{taxPortion.toFixed(2)}</b></span>
                                                                        <span className="text-xs text-gray-500">CGST: ₹{cgst.toFixed(2)} | SGST: ₹{sgst.toFixed(2)}</span>
                                                                    </div>
                                                                    <div className="flex-1 flex flex-col gap-1 border-l border-gray-200 pl-4 mt-2 md:mt-0">
                                                                        <span>Base Price: <b className="text-blue-700">₹{basePrice.toFixed(2)}</b></span>
                                                                        <span className="text-xs text-gray-400">(Pre-tax amount)</span>
                                                                        <span>Total Payable: <b className="text-green-700">₹{Math.round(totalPayable)}</b></span>
                                                                        <span className="text-xs text-gray-400">(Price after discount, tax included, rounded)</span>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                        
                                                        {/* Display Different Offline Pricing if available */}
                                                        {option.offlinePrice && (() => {
                                                            // Calculate offline GST values dynamically
                                                            const offlineDiscountPrice = Number(option.offlinePrice.discountPrice);
                                                            let offlineGstAmount = 0, offlineCgst = 0, offlineSgst = 0, offlineBasePrice = 0, offlineTotalPayable = 0, offlineTaxPortion = 0;
                                                            
                                                            if (gstType === "exclusive") {
                                                                offlineGstAmount = offlineDiscountPrice * (gstPercent / 100);
                                                                offlineCgst = offlineSgst = offlineGstAmount / 2;
                                                                offlineTotalPayable = offlineDiscountPrice + offlineGstAmount;
                                                            } else {
                                                                offlineTaxPortion = offlineDiscountPrice * (gstPercent / (100 + gstPercent));
                                                                offlineCgst = offlineSgst = offlineTaxPortion / 2;
                                                                offlineBasePrice = offlineDiscountPrice - offlineTaxPortion;
                                                                offlineTotalPayable = offlineDiscountPrice;
                                                            }
                                                            
                                                            return (
                                                                <div className="mt-3 pt-3 border-t border-purple-200">
                                                                    <div className="flex items-center gap-2 mb-2">
                                                                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">Different Offline Pricing</span>
                                                                    </div>
                                                                    <div className="flex flex-wrap gap-3 text-sm text-gray-700 mb-2">
                                                                        <div className="flex items-center gap-1">
                                                                            <span className="text-gray-500">Offline Original:</span>
                                                                            <span className="font-semibold text-gray-800">₹{option.offlinePrice.originalPrice}</span>
                                                                        </div>
                                                                        <div className="flex items-center gap-1">
                                                                            <span className="text-gray-500">Offline Discount:</span>
                                                                            <span className="font-semibold text-orange-600">{option.offlinePrice.discount}%</span>
                                                                        </div>
                                                                        <div className="flex items-center gap-1">
                                                                            <span className="text-gray-500">Offline Price:</span>
                                                                            <span className="font-bold text-purple-600">₹{option.offlinePrice.discountPrice}</span>
                                                                        </div>
                                                                    </div>
                                                                    {/* Offline GST Breakdown - Calculated Dynamically */}
                                                                    <div className="flex flex-col md:flex-row md:items-center md:gap-6 text-sm text-gray-700 bg-purple-50 rounded-lg p-3">
                                                                        {gstType === "exclusive" ? (
                                                                            <>
                                                                                <div className="flex-1 flex flex-col gap-1">
                                                                                    <span>Offline GST: <b>₹{offlineGstAmount.toFixed(2)}</b></span>
                                                                                    <span className="text-xs text-gray-500">CGST: ₹{offlineCgst.toFixed(2)} | SGST: ₹{offlineSgst.toFixed(2)}</span>
                                                                                </div>
                                                                                <div className="flex-1 flex flex-col gap-1 border-l border-purple-200 pl-4 mt-2 md:mt-0">
                                                                                    <span>Offline Total Payable: <b className="text-purple-700">₹{Math.round(offlineTotalPayable)}</b></span>
                                                                                    <span className="text-xs text-gray-400">(Price after discount + GST, rounded)</span>
                                                                                </div>
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <div className="flex-1 flex flex-col gap-1">
                                                                                    <span>Offline GST Portion: <b>₹{offlineTaxPortion.toFixed(2)}</b></span>
                                                                                    <span className="text-xs text-gray-500">CGST: ₹{offlineCgst.toFixed(2)} | SGST: ₹{offlineSgst.toFixed(2)}</span>
                                                                                </div>
                                                                                <div className="flex-1 flex flex-col gap-1 border-l border-purple-200 pl-4 mt-2 md:mt-0">
                                                                                    <span>Offline Base Price: <b className="text-purple-700">₹{offlineBasePrice.toFixed(2)}</b></span>
                                                                                    <span className="text-xs text-gray-400">(Pre-tax amount)</span>
                                                                                    <span>Offline Total Payable: <b className="text-purple-700">₹{Math.round(offlineTotalPayable)}</b></span>
                                                                                    <span className="text-xs text-gray-400">(Price after discount, tax included, rounded)</span>
                                                                                </div>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })()}

                                                        {/* Display Different Marketplace (B2B) Pricing if available */}
                                                        {product.isMarketplaceProduct && option.marketplacePrice && (() => {
                                                            const marketplaceDiscountPrice = Number(option.marketplacePrice.discountPrice);
                                                            let mpGstAmount = 0, mpCgst = 0, mpSgst = 0, mpBasePrice = 0, mpTotalPayable = 0, mpTaxPortion = 0;

                                                            if (gstType === "exclusive") {
                                                                mpGstAmount = marketplaceDiscountPrice * (gstPercent / 100);
                                                                mpCgst = mpSgst = mpGstAmount / 2;
                                                                mpTotalPayable = marketplaceDiscountPrice + mpGstAmount;
                                                            } else {
                                                                mpTaxPortion = marketplaceDiscountPrice * (gstPercent / (100 + gstPercent));
                                                                mpCgst = mpSgst = mpTaxPortion / 2;
                                                                mpBasePrice = marketplaceDiscountPrice - mpTaxPortion;
                                                                mpTotalPayable = marketplaceDiscountPrice;
                                                            }

                                                            return (
                                                                <div className="mt-3 pt-3 border-t border-violet-200">
                                                                    <div className="flex items-center gap-2 mb-2">
                                                                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-violet-100 text-violet-700">Marketplace (B2B) Pricing</span>
                                                                    </div>
                                                                    <div className="flex flex-wrap gap-3 text-sm text-gray-700 mb-2">
                                                                        <div className="flex items-center gap-1">
                                                                            <span className="text-gray-500">Marketplace Original:</span>
                                                                            <span className="font-semibold text-gray-800">₹{option.marketplacePrice.originalPrice}</span>
                                                                        </div>
                                                                        <div className="flex items-center gap-1">
                                                                            <span className="text-gray-500">Marketplace Discount:</span>
                                                                            <span className="font-semibold text-orange-600">{option.marketplacePrice.discount}%</span>
                                                                        </div>
                                                                        <div className="flex items-center gap-1">
                                                                            <span className="text-gray-500">Marketplace Price:</span>
                                                                            <span className="font-bold text-violet-600">₹{option.marketplacePrice.discountPrice}</span>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex flex-col md:flex-row md:items-center md:gap-6 text-sm text-gray-700 bg-violet-50 rounded-lg p-3">
                                                                        {gstType === "exclusive" ? (
                                                                            <>
                                                                                <div className="flex-1 flex flex-col gap-1">
                                                                                    <span>Marketplace GST: <b>₹{mpGstAmount.toFixed(2)}</b></span>
                                                                                    <span className="text-xs text-gray-500">CGST: ₹{mpCgst.toFixed(2)} | SGST: ₹{mpSgst.toFixed(2)}</span>
                                                                                </div>
                                                                                <div className="flex-1 flex flex-col gap-1 border-l border-violet-200 pl-4 mt-2 md:mt-0">
                                                                                    <span>Marketplace Total Payable: <b className="text-violet-700">₹{Math.round(mpTotalPayable)}</b></span>
                                                                                    <span className="text-xs text-gray-400">(Price after discount + GST, rounded)</span>
                                                                                </div>
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <div className="flex-1 flex flex-col gap-1">
                                                                                    <span>Marketplace GST Portion: <b>₹{mpTaxPortion.toFixed(2)}</b></span>
                                                                                    <span className="text-xs text-gray-500">CGST: ₹{mpCgst.toFixed(2)} | SGST: ₹{mpSgst.toFixed(2)}</span>
                                                                                </div>
                                                                                <div className="flex-1 flex flex-col gap-1 border-l border-violet-200 pl-4 mt-2 md:mt-0">
                                                                                    <span>Marketplace Base Price: <b className="text-violet-700">₹{mpBasePrice.toFixed(2)}</b></span>
                                                                                    <span className="text-xs text-gray-400">(Pre-tax amount)</span>
                                                                                    <span>Marketplace Total Payable: <b className="text-violet-700">₹{Math.round(mpTotalPayable)}</b></span>
                                                                                    <span className="text-xs text-gray-400">(Price after discount, tax included, rounded)</span>
                                                                                </div>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>

                                {/* Details Panels */}
                                <div className="space-y-6">

                                    {/* Delivery Options */}
                                    {/* <div className="p-6 bg-white rounded-2xl shadow-md border border-gray-100">
                                        <h2 className="text-xl font-bold text-gray-900 mb-6">Delivery Options</h2>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            {deliveryOptionsData.map((option, i) => (
                                                <div key={i} className="p-4 bg-indigo-50/50 rounded-xl border border-indigo-100">
                                                    <div className="flex justify-between items-start mb-1">
                                                        <h3 className="font-bold text-indigo-800 text-lg">{option.label}</h3>
                                                        {option.kmsRange && (
                                                            <span className="px-3 py-1 bg-indigo-100 text-indigo-800 font-bold rounded-full text-sm text-center">
                                                                {option.kmsRange.label}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-sm text-gray-800 mb-4">{option.message}</p>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                        {option.rangeOptions?.map((range, j) => (
                                                            <div key={j} className="p-3 bg-white rounded-lg border border-indigo-100">
                                                                <div className="flex justify-between items-center">
                                                                    <span className="text-sm text-gray-800">
                                                                        {range.from}-{range.to} km
                                                                    </span>
                                                                    <span className="font-semibold text-indigo-600">
                                                                        ₹{range.price}/km
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div> */}


                                    {/* Specifications */}
                                    <div className="bg-white rounded-2xl shadow-md border border-gray-100">
                                        <div
                                            className="flex justify-between items-center cursor-pointer p-5 rounded-lg"
                                            onClick={() => setIsOpen(!isOpen)}
                                        >
                                            <h2 className="text-lg font-bold text-gray-700">Specifications</h2>
                                            <span className="text-xl text-gray-700 mt-1">
                                                {isOpen ? <FaChevronUp /> : <FaChevronDown />}
                                            </span>
                                        </div>
                                        {isOpen && (
                                            <div className="space-y-4 p-6 pt-0">
                                                {[
                                                    { icon: '🧺', title: 'Materials', content: product.materials },
                                                    { icon: '🥄', title: 'Ingredients', content: product.ingredients },
                                                    { icon: '💡', title: 'Uses', content: product.uses },
                                                    { icon: '📖', title: 'Instructions', content: product.instructions },
                                                    { icon: '📑', title: 'Full Details', content: product.fullDetails }
                                                ].map((spec, i, specs) => (
                                                    <div
                                                        key={i}
                                                        className={`${i === specs.length - 1 ? 'border-b-0' : 'pb-4'} border-b border-gray-100`}
                                                    >
                                                        <div className="flex items-start gap-4">
                                                            <span className="text-2xl">{spec.icon}</span>
                                                            <div>
                                                                <h3 className="font-semibold text-gray-800 mb-2">{spec.title}</h3>
                                                                <p className="text-gray-600 leading-relaxed line-clamp-2">{spec.content}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Additional Sections */}
                            {/* <div>
                                <Orders
                                    products={[product]}
                                    activeTab={activeTab}
                                    setActiveTab={setActiveTab}
                                    loading={loading}
                                    setShowStatusModal={setShowStatusModal}
                                    setExpandedCard={setExpandedCard}
                                    expandedCard={expandedCard}
                                    showNavigateBack={false}
                                    classOuterDiv={"py-0"}
                                    classInnerDiv={"mb-6"}
                                />

                                {showStatusModal && (
                                    <StatusChangeModal
                                        showStatusModal={showStatusModal}
                                        setShowStatusModal={setShowStatusModal}
                                        selectedStatus={selectedStatus}
                                        setSelectedStatus={setSelectedStatus}
                                        handleChangeStatus={handleChangeStatus}
                                    />
                                )}
                            </div> */}
                        </div>
                    )}

                    {/* Delete Confirmation Modal */}
                    <ConfirmModal
                        visible={showDeleteModal}
                        message={
                            <div className="text-center">
                                <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
                                    <FaExclamationTriangle className="text-2xl text-red-500" />
                                </div>
                                <h3 className="text-lg font-bold text-gray-800 mb-2">Delete Product?</h3>
                                <p className="text-gray-600">
                                    Are you sure you want to delete <strong>"{product?.productName}"</strong>? This will also delete the respective inventory. This action cannot be undone.
                                </p>
                            </div>
                        }
                        onConfirm={handleDeleteProduct}
                        onCancel={() => setShowDeleteModal(false)}
                        cancelButtonText="Cancel"
                        confirmButtonText={deleteLoading ? "Deleting..." : "Delete Product"}
                    />
                </div>
            )}
        </div >
    );
};

export default ProductOverview;